import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Injectable,
  InjectionToken,
  PLATFORM_ID,
  PendingTasks,
  TransferState,
  inject,
  makeStateKey,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';

export interface CmsPageBlock {
  type: string;
  order: number;
  props: Record<string, any>;
}

export interface CmsPageData {
  cms_page_id: number;
  lang: string;
  slug: string;
  title: string;
  seo_title: string;
  seo_description: string;
  og_image: string;
  status: string;
  blocks: CmsPageBlock[];
}

interface CmsPageApiResponse {
  type: string;
  data: CmsPageData | null;
}

/**
 * En SSR resuelve la página directamente de BD; en browser llama a /api/cms-page.
 *
 * SIN `providedIn: 'root'`/factory a propósito: commonEngine.render inyecta el
 * provider a nivel PLATFORM, y si el token tuviera binding en root, el root lo
 * resolvería primero (devolviendo el default) y nunca llegaría al platform. Al no
 * tener binding en root, la inyección sube hasta el platform y encuentra el real.
 * En browser no hay provider → se inyecta `optional` y getPage usa la vía HTTP.
 */
export const GET_CMS_PAGE_FN = new InjectionToken<
  (slug: string, lang: string, preview?: boolean) => Promise<CmsPageData | null>
>('GET_CMS_PAGE_FN');

/**
 * Fija el código de estado HTTP de la respuesta en SSR (para devolver 404 en
 * páginas no encontradas). Proveído a nivel platform en server.ts; ausente en
 * browser (se inyecta optional y no se llama).
 */
export const SET_RESPONSE_STATUS = new InjectionToken<(code: number) => void>(
  'SET_RESPONSE_STATUS'
);

function cmsPageStateKey(slug: string, lang: string, preview: boolean) {
  return makeStateKey<CmsPageData | null>(
    `cms-page:${slug}:${lang}${preview ? ':preview' : ''}`
  );
}

@Injectable({ providedIn: 'root' })
export class CmsPageDataService {
  private platformId = inject(PLATFORM_ID);
  private getCmsPageFn = inject(GET_CMS_PAGE_FN, { optional: true });
  private http = inject(HttpClient);
  private pendingTasks = inject(PendingTasks);
  private transferState = inject(TransferState);

  /**
   * Estado transferido del SSR para la primera carga en browser (o `undefined`
   * si no lo hay). Es SÍNCRONO a propósito: el componente lo aplica antes del
   * primer render para que la hidratación case con el HTML del servidor.
   * `null` transferido significa "página no encontrada" (también cuenta).
   * Consume la clave: navegaciones posteriores vuelven a la vía HTTP.
   */
  getTransferred(
    slug: string,
    lang = 'es',
    preview = false
  ): CmsPageData | null | undefined {
    const key = cmsPageStateKey(slug, lang.split('-')[0], preview);
    if (!this.transferState.hasKey(key)) return undefined;
    const value = this.transferState.get<CmsPageData | null>(key, null);
    this.transferState.remove(key);
    return value;
  }

  async getPage(
    slug: string,
    lang = 'es',
    preview = false,
    token = ''
  ): Promise<CmsPageData | null> {
    const normalizedLang = lang.split('-')[0];

    if (isPlatformServer(this.platformId)) {
      if (!this.getCmsPageFn) return null;
      // PendingTasks marca la app como "ocupada" hasta que la query termina:
      // sin esto el SSR serializa antes de que resuelva la BD (sale el spinner).
      const done = this.pendingTasks.add();
      try {
        const page = await this.getCmsPageFn(slug, normalizedLang, preview);
        this.transferState.set(
          cmsPageStateKey(slug, normalizedLang, preview),
          page
        );
        return page;
      } finally {
        done();
      }
    }

    try {
      let url = `/api/cms-page/${encodeURIComponent(slug)}/${normalizedLang}`;
      if (preview) {
        url += `?preview=1&token=${encodeURIComponent(token)}`;
      }
      const response = await firstValueFrom(
        this.http.get<CmsPageApiResponse>(url)
      );
      return response.data ?? null;
    } catch {
      return null;
    }
  }
}
